/**
 * How a chip in the transcript reaches the send path.
 *
 * The inline route draws its answers **inside a turn**, which means inside `DeckThread` — a
 * memoised component two levels below the pane, whose props are deliberately kept to primitives and
 * things the pane holds still, because both existing call sites of the real chat warn that an
 * unstable prop re-renders the whole thread once per animation frame while a reply streams.
 *
 * Threading a handler down would mean a new prop on `DeckThread`, a new argument on its render loop,
 * and a new thing every future caller has to remember to pass. A context costs one provider in the
 * pane and reaches the chips through `memo` without widening anybody's interface. It is also honest
 * about what is going on: there is exactly one question in flight at a time, and answering it is a
 * property of the CONVERSATION, not of the block that happens to be drawing it.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ClarificationAnswer, ContextRequestAction } from '~/types/planning';

export interface QuestionAnswerApi {
  /** Answer or refuse the request the agent is waiting on. */
  answer: (action: ContextRequestAction, answers: ClarificationAnswer[]) => void;
  /** An answer is on the wire. Controls stop taking presses rather than queueing them. */
  submitting: boolean;
}

const QuestionAnswerContext = createContext<QuestionAnswerApi | null>(null);

export function QuestionAnswerProvider({
  answer,
  submitting,
  children,
}: QuestionAnswerApi & { children: ReactNode }) {
  // Memoised on the two values it carries: the provider sits above the thread, so a fresh object
  // every render would be a fresh context value every render, which is the one way a context can
  // out-render the prop it replaced.
  const value = useMemo<QuestionAnswerApi>(() => ({ answer, submitting }), [answer, submitting]);
  return <QuestionAnswerContext.Provider value={value}>{children}</QuestionAnswerContext.Provider>;
}

/**
 * Null outside a provider, and that is a legal state rather than an error: a story or a preview can
 * render a settled transcript with no send path behind it, and an answered question is just words.
 */
export function useQuestionAnswer(): QuestionAnswerApi | null {
  return useContext(QuestionAnswerContext);
}
